import {useCallback, useEffect, useRef, useState} from 'react';
import {initialPublicMetalQuotes, normalizePublicMetalQuotes, retainPublicMetalQuotes} from './publicMetalQuotes';

export const PUBLIC_METAL_QUOTES_ENDPOINT = '/api/public/metal-quotes';
export const PUBLIC_METAL_QUOTES_REFRESH_MS = 60000;
export const PUBLIC_METAL_QUOTES_TIMEOUT_MS = 20000;
const RESUME_THROTTLE_MS = 15000;

export default function usePublicMetalQuotes() {
  const [state, setState] = useState(() => ({quotes: initialPublicMetalQuotes(), loading: true, error: null}));
  const refreshRef = useRef(null);
  const refresh = useCallback(() => refreshRef.current?.() || Promise.resolve(null), []);

  useEffect(() => {
    let disposed = false;
    let active = null;
    let lastAttemptAt = -Infinity;
    const requestQuotes = () => {
      if (disposed) return Promise.resolve(null);
      if (active) return active.promise;
      const request = {controller: new AbortController(), timer: null, promise: null};
      active = request;
      lastAttemptAt = Date.now();
      setState(current => ({...current, loading: true}));
      request.promise = (async () => {
        try {
          const timeout = new Promise((_, reject) => {
            request.timer = setTimeout(() => {reject(new Error('timeout')); request.controller.abort();}, PUBLIC_METAL_QUOTES_TIMEOUT_MS);
          });
          const payload = await Promise.race([(async () => {
            const response = await fetch(PUBLIC_METAL_QUOTES_ENDPOINT, {
              method: 'GET', cache: 'no-store', credentials: 'omit', headers: {Accept: 'application/json'}, signal: request.controller.signal,
            });
            if (!response.ok) throw new Error('connection');
            return response.json();
          })(), timeout]);
          const quotes = normalizePublicMetalQuotes(payload);
          if (!quotes) throw new Error('invalid-response');
          if (disposed || active !== request) return null;
          setState(current => ({quotes: retainPublicMetalQuotes(current.quotes, quotes), loading: false,
            error: Object.values(quotes).some(quote => quote.connection !== 'current') ? 'partial' : null}));
          return quotes;
        } catch (error) {
          if (disposed || active !== request) return null;
          setState(current => ({quotes: retainPublicMetalQuotes(current.quotes), loading: false,
            error: error?.message === 'timeout' ? 'timeout' : 'connection'}));
          return null;
        } finally {
          clearTimeout(request.timer);
          if (active === request) active = null;
        }
      })();
      return request.promise;
    };
    const refreshOnResume = () => {
      if (document.visibilityState !== 'hidden' && Date.now() - lastAttemptAt >= RESUME_THROTTLE_MS) requestQuotes();
    };
    refreshRef.current = requestQuotes;
    if (document.visibilityState !== 'hidden') requestQuotes();
    const timer = setInterval(() => {if (document.visibilityState !== 'hidden') requestQuotes();}, PUBLIC_METAL_QUOTES_REFRESH_MS);
    document.addEventListener('visibilitychange', refreshOnResume);
    window.addEventListener('focus', refreshOnResume);
    return () => {
      disposed = true;
      refreshRef.current = null;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshOnResume);
      window.removeEventListener('focus', refreshOnResume);
      if (active) {clearTimeout(active.timer); active.controller.abort();}
    };
  }, []);
  return {...state, refresh};
}
